'use client';
import { useTranslation } from '@whoop/i18n/lang/client';
import { AmplitudeEvents } from 'whoop-analytics/lib/types';
import { Drawer } from 'ui/components/Drawer/drawer';
import { useShallow } from 'zustand/react/shallow';
import { useState, useEffect, useRef } from 'react';
import { ScrollArea } from 'ui/components/ScrollArea/ScrollArea';
import { getBNPLTotalToUseV2 } from 'ui/utils/totalsHelpers';
import { Separator } from 'ui/components/Separator/Separator';
import clsx from 'clsx';
import MakeThisAGiftCheckbox from '../JFOplPage/JFOplContent/MakeThisAGiftCheckbox/MakeThisAGiftCheckbox';
import { useAnalytics } from '@/hooks/analytics';
import { useSiteStore } from '@/store/SiteStoreProvider';
import BillingDetails from '@/components/Cart/BillingDetails/BillingDetails';
import type { CartButtonVariants } from '@/components/Cart/CartCTAButtons/CartCTAButtons';
import { CartCTAButtons } from '@/components/Cart/CartCTAButtons/CartCTAButtons';
import {
  cartHasPrepaidMembership,
  hasProductTypeInCartItemGroups,
  isMembershipAndDeviceInCartItems,
} from '@/components/Cart/cart.utils';
import { useGetCurrentPageMaverick } from '@/hooks/getCurrentPage';
import StripeBNPLElement from '@/components/StripeBNPLElement/StripeBNPLElement';
import CartGroups from '@/components/Cart/CartGroups/CartGroups';
import { isJFStepsPage } from '@/lib/utils/maverickHelpers';
import { ProductType, TRIAL, UPGRADE_STEPS } from '@/lib/constants/maverick';
import {
  EXPERIMENT_JF_CART_ITEMS,
  EXPERIMENT_JF_TRIAL_BLOCK,
  TRIAL_BLOCK_VARIANT,
} from '@/lib/constants/experiments';

export interface CartComponentProps {
  CTAButtonsVariant: CartButtonVariants;
  upcycleExperimentVariant?: string;
}

export default function Cart({
  CTAButtonsVariant,
  upcycleExperimentVariant,
}: CartComponentProps): JSX.Element | null {
  const {
    isCartOpen,
    setIsCartOpen,
    cart,
    isRafGiftFlow,
    currency,
    language,
    country,
    toggleAsAGift,
    asAGift,
    skipMemSelectFlow,
    experimentVariants,
  } = useSiteStore(
    useShallow((state) => ({
      isCartOpen: state.isCartOpen,
      setIsCartOpen: state.setIsCartOpen,
      cart: state.cart,
      isRafGiftFlow: state.isRafGiftFlow,
      asAGift: state.asAGift,
      currency: state.currency,
      language: state.language,
      country: state.country,
      toggleAsAGift: state.toggleAsAGift,
      skipMemSelectFlow: state.skipMemSelectFlow,
      experimentVariants: state.experimentVariants,
    })),
  );
  const { trackAmplitudeEvent } = useAnalytics();
  const currentPage = useGetCurrentPageMaverick();
  const { t } = useTranslation('cart');
  const [showCartCTAs, setShowCartCTAs] = useState(false);
  const showBNPLMessaging =
    cartHasPrepaidMembership(cart.cartItemGroups) && country === 'US';
  const [showStickyCTA, setShowStickyCTA] = useState<boolean>();
  const [showTrialBlock, setShowTrialBlock] = useState(false);
  const nonStickyCTARef = useRef<HTMLDivElement>(null);
  const { id: cartId, billingDetails, cartItemGroups } = cart;

  useEffect(() => {
    const showUpgradeCartCTAs =
      currentPage === UPGRADE_STEPS.OPTIONS ||
      currentPage === UPGRADE_STEPS.UPCYCLE ||
      hasProductTypeInCartItemGroups(
        cart.cartItemGroups,
        ProductType.EXTENSION,
      ) ||
      hasProductTypeInCartItemGroups(
        cart.cartItemGroups,
        ProductType.UPGRADE_FEE,
      );

    const showJoinFlowCartCTAs =
      isMembershipAndDeviceInCartItems(cart.cartItemGroups) ||
      (skipMemSelectFlow === TRIAL &&
        hasProductTypeInCartItemGroups(
          cart.cartItemGroups,
          ProductType.MEMBERSHIP,
        ));
    setShowCartCTAs(showJoinFlowCartCTAs || showUpgradeCartCTAs);
  }, [cartItemGroups]);

  useEffect(() => {
    if (isCartOpen) {
      trackAmplitudeEvent(AmplitudeEvents.ViewedCart);
    }
  }, [isCartOpen]);

  useEffect(() => {
    const trialBlockVariant = experimentVariants[EXPERIMENT_JF_TRIAL_BLOCK];
    setShowTrialBlock(trialBlockVariant === TRIAL_BLOCK_VARIANT);
  }, [experimentVariants]);

  const checkNonStickyCTAVisibility = () => {
    if (!nonStickyCTARef.current) return;

    // Expected behaviour:
    // If the non-sticky CTA at the bottom of the cart is in view: the sticky CTA doesn't show
    // If the non-sticky CTA at the bottom of the cart is in not in view: the sticky CTA does show
    const rect = nonStickyCTARef.current.getBoundingClientRect();
    const isNonStickyCTAPartiallyVisible =
      rect.top < (window.visualViewport?.height || window.innerHeight) &&
      rect.bottom > 0;
    setShowStickyCTA(!isNonStickyCTAPartiallyVisible);
  };

  if (!cartId) {
    return null;
  }

  const hasPrepaidMembershipInCart = cartHasPrepaidMembership(
    cart.cartItemGroups,
  );

  const showGiftCheckbox = Boolean(
    isJFStepsPage(currentPage) && hasPrepaidMembershipInCart && !isRafGiftFlow,
  );

  const isUpcycleExperimentVariant = upcycleExperimentVariant === 'experiment';

  return (
    <>
      {/* Cart Drawer Wrapper */}
      <Drawer
        blurBackground={false}
        className='bg-gray-50'
        isOpen={isCartOpen}
        key='cart-drawer'
        onClose={() => {
          trackAmplitudeEvent(AmplitudeEvents.CloseCart);
        }}
        onResize={checkNonStickyCTAVisibility}
        onScroll={checkNonStickyCTAVisibility}
        position='right'
        setIsOpen={setIsCartOpen}
        title={t('yourCart')}
      >
        {/* Main Cart Contents */}
        <ScrollArea
          className='max-h-full max-w-full'
          onContentLoaded={checkNonStickyCTAVisibility}
        >
          <div
            className={clsx(
              'flex flex-col items-center justify-center space-y-5 sm:w-[360px]',
            )}
            data-testid='cart'
          >
            {/* Cart Items */}
            <CartGroups
              cartId={cartId}
              cartItemGroups={cartItemGroups}
              country={country}
              hideHsaFsaBadge={asAGift}
              cartItemsExperimentVariant={
                experimentVariants[EXPERIMENT_JF_CART_ITEMS]
              }
            />
            <div
              className={clsx(
                'w-full space-y-4 md:space-y-6',
                'rounded-xl bg-white p-4',
              )}
            >
              {/* Billing Details */}
              <BillingDetails billingDetails={billingDetails} />
              {showBNPLMessaging ? (
                <>
                  <Separator />
                  <div className='w-full'>
                    <StripeBNPLElement
                      cartVariant
                      countryCode='US' // This is US only
                      currency={currency}
                      language={language}
                      price={getBNPLTotalToUseV2(billingDetails)}
                    />
                  </div>
                </>
              ) : null}
              {/* Cart CTA Buttons */}
              <div className={clsx('m-auto', 'w-full')} ref={nonStickyCTARef}>
                {showCartCTAs ? (
                  <CartCTAButtons
                    isUpcycleExperimentVariant={isUpcycleExperimentVariant}
                    onCartClose={() => setIsCartOpen(false)}
                    skipMemSelectFlow={skipMemSelectFlow}
                    showTrialBlock={showTrialBlock}
                    variant={CTAButtonsVariant}
                  />
                ) : null}
              </div>
            </div>
            {showGiftCheckbox ? (
              <MakeThisAGiftCheckbox
                asAGift={asAGift}
                toggleAsAGift={toggleAsAGift}
              />
            ) : null}
            <div className={clsx('py-20')}>
              {/* Spacer for viewing buttons with sticky CTA */}
            </div>
          </div>
        </ScrollArea>

        <div
          className={clsx(
            'sticky -bottom-5 m-auto rounded-lg bg-white p-4 transition-opacity duration-300 ease-in-out',
            showStickyCTA ? 'opacity-100' : 'hidden opacity-0',
          )}
        >
          {showCartCTAs ? (
            <CartCTAButtons
              isUpcycleExperimentVariant={isUpcycleExperimentVariant}
              onCartClose={() => setIsCartOpen(false)}
              showOnlyPrimaryButton
              skipMemSelectFlow={skipMemSelectFlow}
              showTrialBlock={showTrialBlock}
              variant={CTAButtonsVariant}
            />
          ) : null}
        </div>
      </Drawer>
    </>
  );
}
